import { Table, Badge, Text, ScrollArea, Box, Stack } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { EmptyState } from '@lighthouse/design'
import type { ActivityLogEntry } from '../../types/api'
import { DETAIL_ADMIN_ONLY, FINAL_ATTEMPT_ONLY, errorKind, errorSummary } from '../../utils/format'
import type { ErrorKind } from '../../utils/format'
import { errorTextView } from '../../utils/haltDetail'

interface JobHistoryProps {
  /** Undefined until the history is served. */
  activities: ActivityLogEntry[] | undefined
  isLoading: boolean
  /** Null unless the read failed. A failed read is never "no activity". */
  error: unknown
}

/** What a failed history read says, by kind. None of it is a verdict on whether a job ran. */
function historyErrorCopy(kind: ErrorKind): { title: string; message: string } {
  const noVerdict = 'That says nothing about whether any ETL job ran.'
  switch (kind) {
    case 'unauthorized':
      return { title: 'Sign-in was not accepted', message: `The API did not accept this session, so no activity was read. ${noVerdict}` }
    case 'forbidden':
      return {
        title: 'The API refused this request',
        message: 'ETL activity is served to any signed-in user, so this is not a missing Lighthouse.Admin role; '
          + `something in front of the API refused it. ${noVerdict}`,
      }
    case 'network':
      return { title: 'Could not reach the API', message: `The request never got an answer. ${noVerdict}` }
    case 'server':
      return { title: 'The API failed while reading ETL activity', message: `It answered with a server error. ${noVerdict}` }
    case 'not-found':
      return { title: 'ETL activity is not served here', message: 'This API does not have the activity endpoint.' }
    default:
      return { title: 'Could not load the job history', message: noVerdict }
  }
}

function HistoryError({ error }: { error: unknown }) {
  const copy = historyErrorCopy(errorKind(error))
  const said = errorSummary(error)
  return (
    <div role="alert" data-state="history-error">
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} color="var(--mantine-color-red-6)" />}
        title={copy.title}
        message={<>{copy.message}{said && <> The server says: {said}</>}</>}
      />
    </div>
  )
}

/**
 * A failed row's recorded error. The server withholds it from a non-administrator when it is a copy of a halt's
 * detail (D8) and says so with `errorMessageWithheld` — then this says DETAIL_ADMIN_ONLY, dimmed, rather than leaving
 * a failure with no word. Nothing at all when none was recorded.
 */
function EntryError({ entry }: { entry: ActivityLogEntry }) {
  const view = errorTextView(entry.errorMessage, entry.errorMessageWithheld)
  if (view.kind === 'none') return null
  if (view.kind === 'withheld') {
    return (
      <Text size="xs" c="dimmed" data-state="detail-withheld">
        {DETAIL_ADMIN_ONLY}
      </Text>
    )
  }
  return (
    <Text size="xs" c="red" lineClamp={2} title={view.text} style={{ maxWidth: 320 }} data-state="error-text">
      {view.text}
    </Text>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins < 60) return `${mins}m ${Math.round(secs)}s`
  const hrs = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return `${hrs}h ${remainingMins}m`
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffHours < 24) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
    ' ' +
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function getEventTypeBadge(eventType: string): { color: string; label: string } {
  switch (eventType) {
    case 'EtlCompleted':
      return { color: 'teal', label: 'Entity ETL' }
    case 'EtlFailed':
      return { color: 'red', label: 'Entity ETL' }
    case 'EtlCancelled':
      // Its run was cancelled: neither a success nor a failure, and counted as neither.
      return { color: 'gray', label: 'Entity ETL (cancelled)' }
    case 'OrchestratorCompleted':
      return { color: 'blue', label: 'Orchestrator' }
    case 'EtlJobStarted':
      return { color: 'indigo', label: 'Job Started' }
    case 'EtlJobCompleted':
      return { color: 'violet', label: 'Job Completed' }
    default:
      return { color: 'gray', label: eventType }
  }
}

export function JobHistory({ activities, isLoading, error }: JobHistoryProps) {
  if (error != null) {
    return <HistoryError error={error} />
  }

  // `|| !activities` is load-bearing: between retries a failed read has no data and is not loading.
  if (isLoading || !activities) {
    return (
      <Stack gap="md" data-state="history-loading" aria-busy="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            style={{
              height: 32,
              background: 'var(--color-border)',
              opacity: 0.3,
              borderRadius: 4,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
      </Stack>
    )
  }

  if (activities.length === 0) {
    return (
      <Box
        data-state="history-empty"
        style={{
          height: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <Text size="sm" c="dimmed" ta="center">
          No historical activity found.
        </Text>
      </Box>
    )
  }

  return (
    <ScrollArea h={400} type="auto" offsetScrollbars>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Entity / Module</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>
              {/* RecordsProcessed is the FINAL attempt's inserted plus updated: a resumed entity under-counts. */}
              <span title={FINAL_ATTEMPT_ONLY} style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                Records (final attempt)
              </span>
            </Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Duration</Table.Th>
            <Table.Th>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {activities.map((entry) => {
            const typeBadge = getEventTypeBadge(entry.eventType)
            return (
              <Table.Tr key={entry.id}>
                <Table.Td>
                  <Text size="sm">{formatTimestamp(entry.eventTimestamp)}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={typeBadge.color} variant="light" size="sm">
                    {typeBadge.label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" truncate style={{ maxWidth: 200 }}>
                    {entry.entityName ?? entry.module ?? 'N/A'}
                  </Text>
                  {entry.module && entry.entityName && (
                    <Text size="xs" c="dimmed">
                      {entry.module}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">
                    {entry.recordsProcessed != null
                      ? entry.recordsProcessed.toLocaleString()
                      : '-'}
                  </Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">{formatDuration(entry.durationSeconds)}</Text>
                </Table.Td>
                <Table.Td>
                  {entry.success ? (
                    <Badge color="green" variant="light" size="sm">
                      Success
                    </Badge>
                  ) : (
                    <Badge color="red" variant="light" size="sm">
                      Failed
                    </Badge>
                  )}
                  {entry.wasReset && (
                    <Badge color="orange" variant="light" size="xs" ml={4}>
                      Reset
                    </Badge>
                  )}
                  <EntryError entry={entry} />
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  )
}
